import 'react-phone-input-2/lib/material.css';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useEffect, useRef } from 'react';
import { convertIntoArray, getContentKeyAccToLang } from '../../../utils/utils';
import { Chips, ErrorText, PhoneInput, RowView } from '../../CorporateForm/components';
import Dropdown from '../../../components/Dropdown';
import CustomInput from '../../../components/CustomInput';
import Button from '../../../components/Button';
import dynamicfunction from '../../../utils/dynamicfunction';
import { useNavigate } from 'react-router';
import { logoutUser } from '../../../store/actions/authActions';
import { individual_employer_info_validation_schema } from '../../../validations_schemas/individual_employer_information_schema';
import { getIndEmployerInformationData, postIndEmployerInformation, saveIndKycEmployerInformation } from '../../../store/actions/individualAction';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const EmployerInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isArabic } = dynamicfunction();

  const { individualContent, language } = useSelector((state) => state.content);
  const { ind_post_loading, ind_employer_information_data, ind_employer_information_dropdown_data } = useSelector((state) => state.individual);

  const { client_application_id, is_kyc } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  const getContent = (key) => {
    return getContentKeyAccToLang(individualContent?.employer_information?.data?.attributes, key, isArabic);
  };

  const myArray = convertIntoArray(ind_employer_information_dropdown_data);

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getIndEmployerInformationData(client_application_id));
  }

  const onSubmit = (values) => {
    const api_data = {
      ...ind_employer_information_data,
      nameOfEmployee: values?.nameOfEmployee,
      employersAddress: values?.employersAddress,
      employersPhoneNumber: values?.employersPhoneNumber,
      position: values?.position,
      durationOfEmploymentId: Number(values?.durationOfEmploymentId?.id) ?? null,
      employmentStatusId: values?.employmentStatusId ?? null,
    };

    dispatch(
      is_kyc
        ? saveIndKycEmployerInformation(api_data, (res) => {
            values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
          })
        : postIndEmployerInformation(api_data, (res) => {
            values?.isSaveExist ? dispatch(logoutUser()) : onDone();
          }),
    );
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? {
          ...draftValues.current,
          // Re-pick dropdown objects from the refetched list so labels show in the new language.
          durationOfEmploymentId:
            myArray?.durationOfEmploymentWithCurrentEmployer?.find((item) => item.id === String(draftValues.current?.durationOfEmploymentId?.id)) || null,
        } : {
          employmentStatusId: ind_employer_information_data?.employmentStatusId?.toString() ?? '',
          nameOfEmployee: ind_employer_information_data?.nameOfEmployee ?? '',
          employersAddress: ind_employer_information_data?.employersAddress ?? '',
          employersPhoneNumber: ind_employer_information_data?.employersPhoneNumber ?? '',
          position: ind_employer_information_data?.position ?? '',
          durationOfEmploymentId:
            myArray?.durationOfEmploymentWithCurrentEmployer?.find(
              (item) => item.id === String(ind_employer_information_data?.durationOfEmploymentId),
            ) || null,
          isSaveExist: false,
        }}
        validationSchema={individual_employer_info_validation_schema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, setFieldTouched, touched, errors }) => {
          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <div className='w-full'>
                  <Chips
                    label={getContent('work_field')}
                    value={values?.employmentStatusId}
                    onChange={(e) => {
                      setFieldValue('employmentStatusId', e);
                      setFieldTouched('employmentStatusId', false);
                    }}
                    options={myArray?.employmentStatus}
                  />
                  <ErrorText text={getContent(touched?.employmentStatusId ? errors?.employmentStatusId : '')} />
                </div>
              </RowView>
              {(
                values?.employmentStatusId?.toString() === '38' ||
                values?.employmentStatusId?.toString() === '40' ||
                values?.employmentStatusId?.toString() === '42' ||
                values?.employmentStatusId?.toString() === '1479'
            ) && (
                  <div>
                    <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                      <CustomInput
                        isRounded
                        isRequired={true}
                        maxLength={30}
                        label={getContent('employer_name')}
                        labelDivClass={'h-6 md:h-9 xl:h-6'}
                        placeholder={getContent('enter_name')}
                        value={values?.nameOfEmployee}
                        onChange={(e) => {
                          setFieldValue('nameOfEmployee', e.target.value);
                          setFieldTouched('nameOfEmployee', true, false);
                        }}
                        errorMessage={getContent(touched?.nameOfEmployee ? errors?.nameOfEmployee : '')}
                      />
                      <CustomInput
                        isRounded
                        isRequired={true}
                        maxLength={100}
                        label={getContent('employer_address')}
                        labelDivClass={'h-9 sm:h-6 md:h-9 xl:h-6'}
                        placeholder={getContent('enter_address')}
                        value={values?.employersAddress}
                        onChange={(e) => {
                          setFieldValue('employersAddress', e.target.value);
                          setFieldTouched('employersAddress', true, false);
                        }}
                        errorMessage={getContent(touched?.employersAddress ? errors?.employersAddress : '')}
                      />
                      <div className='w-full flex flex-col'>
                        <PhoneInput
                          label={getContent('employer_phone')}
                          labelDivClass={'h-6 md:h-9 xl:h-6'}
                          labelColor={'text-text-grey'}
                          value={values?.employersPhoneNumber}
                          onChange={(val) => {
                            setFieldValue('employersPhoneNumber', val);
                            setFieldTouched('employersPhoneNumber', true, false);
                          }}
                        />
                        <ErrorText text={getContent(touched?.employersPhoneNumber ? errors?.employersPhoneNumber : '')} />
                      </div>
                    </RowView>

                    <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                      <CustomInput
                        isRounded
                        isRequired={true}
                        maxLength={100}
                        label={getContent('position_title')}
                        placeholder={getContent('enter_position_title')}
                        value={values?.position}
                        onChange={(e) => {
                          setFieldValue('position', e.target.value);
                          setFieldTouched('position', true, false);
                        }}
                        errorMessage={getContent(touched?.position ? errors?.position : '')}
                      />
                      <Dropdown
                        label={getContent('employment_duration')}
                        placeholder={getContent('enter_position_title')}
                        options={myArray?.durationOfEmploymentWithCurrentEmployer}
                        selectedValue={values?.durationOfEmploymentId}
                        valueKey={'name'}
                        isRequired
                        isSearch
                        onSelect={(val) => {
                          setFieldValue('durationOfEmploymentId', val);
                          setFieldTouched('durationOfEmploymentId', false);
                        }}
                        errorMessage={getContent(touched?.durationOfEmploymentId ? errors?.durationOfEmploymentId : '')}
                      />
                      <div className='lg:w-full hidden lg:block'></div>
                    </RowView>
                  </div>
                )}

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>

              <RowView className='gap-3 sm:gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  loading={ind_post_loading}
                  type={'submit'}
                  isDisabled={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default EmployerInformation;
